import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Shield, CheckCircle, AlertCircle, Lock, User } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SINRequest() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sin, setSin] = useState('');

  useEffect(() => {
    loadRequestData();
  }, [token]);

  const loadRequestData = async () => {
    try {
      const response = await axios.get(`${API}/api/agents/sin-request/${token}`);
      setRequestData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired request');
    } finally {
      setLoading(false);
    }
  };

  const formatSIN = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as XXX-XXX-XXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
  };

  const handleSINChange = (e) => {
    const formatted = formatSIN(e.target.value);
    setSin(formatted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate SIN
    const cleanSIN = sin.replace(/-/g, '');
    if (cleanSIN.length !== 9 || !/^\d+$/.test(cleanSIN)) {
      toast.error('Please enter a valid 9-digit SIN');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/agents/sin-request/${token}/submit`, {
        sin: cleanSIN
      });
      setSuccess(true);
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit SIN');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Request Not Found</h2>
              <p className="text-slate-600">{error}</p>
              <p className="text-sm text-slate-500 mt-4">
                If you believe this is an error, please contact your administrator.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">SIN Submitted Successfully</h2>
              <p className="text-slate-600">
                Thank you, {requestData?.agent_name}! Your Social Insurance Number has been securely submitted.
              </p>
              <p className="text-sm text-slate-500 mt-4">
                You can close this window now.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-emerald-600" />
          </div>
          <CardTitle className="text-xl">Secure SIN Submission</CardTitle>
          <CardDescription>
            Utmost Financial Inc. has requested your Social Insurance Number for tax purposes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Agent Info */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">{requestData?.agent_name}</p>
                <p className="text-sm text-slate-500">{requestData?.agent_email}</p>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <Lock className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              Your information is transmitted securely and will only be used for tax reporting purposes.
            </AlertDescription>
          </Alert>

          {/* SIN Form */}
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="sin" className="text-sm font-medium text-slate-700">
                  Social Insurance Number (SIN)
                </Label>
                <Input
                  id="sin"
                  type="text"
                  value={sin}
                  onChange={handleSINChange}
                  placeholder="XXX-XXX-XXX"
                  maxLength={11}
                  className="mt-1 text-center text-lg tracking-wider font-mono"
                  data-testid="sin-input"
                  autoComplete="off"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter your 9-digit SIN. It will be automatically formatted.
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700" 
                disabled={submitting || sin.replace(/-/g, '').length !== 9}
                data-testid="submit-sin-btn"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit SIN Securely'
                )}
              </Button>
            </div>
          </form>

          {/* Expiry Notice */}
          <p className="text-xs text-center text-slate-500 mt-4">
            This link expires on {requestData?.expires_at ? new Date(requestData.expires_at).toLocaleDateString() : 'N/A'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
